import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// @mui
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";

// ZionLadder UI
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import wechatPayLogo from "assets/images/logos/payments/logo-wechatpay.png";
import alipayLogo from "assets/images/logos/payments/logo-alipay.png";
import unionPayLogo from "assets/images/logos/payments/logo-unionpay.png";
import masterCardLogo from "assets/images/logos/payments/logo-mastercard.png";
import visaLogo from "assets/images/logos/payments/logo-visa.png";
import amexLogo from "assets/images/logos/payments/logo-amex.png";
import applepaylogo from "assets/images/logos/payments/logo-applepay.png";

/**
 * PayOptions
 *
 * UI-only component that renders:
 * - Order summary (plan/duration/price)
 * - Payment method radio options (wechat/alipay/card)
 * - Provider radio options (stripe/airwallex/epay)
 * - Action buttons (cancel / continue)
 * - Error message display
 *
 * This component does NOT call APIs and does NOT manage payment state.
 * The parent (Payments/index.js) should pass handlers and state.
 */
function PayOptions({
  // payment selection
  provider,
  onProviderChange,
  method,
  onMethodChange,
  availableMethods,
  availableProviders,
  methodsLoading,

  // actions
  onCancel,
  onContinue,
  onRetry,
  loading,
  disabled,
  isPaymentPending,
  retryDisabled,

  // ui state
  error,
}) {
  const { t } = useTranslation();

  return (
    <>
      <MKBox mt={3}>
        <MKTypography variant="h6" fontWeight="bold">
          {t("payments.payOptions.methodTitle")}
        </MKTypography>

        {methodsLoading ? (
          <MKTypography variant="caption" color="text" display="block" mt={1}>
            {t("common.loading")}
          </MKTypography>
        ) : null}

        <RadioGroup
          value={method}
          onChange={(e) => {
            const nextMethod = e.target.value;
            if (onMethodChange) onMethodChange(nextMethod);
          }}
          sx={{ mt: 1 }}
        >
          {availableMethods.includes("wechatpay") ? (
            <FormControlLabel
              value="wechatpay"
              control={<Radio />}
              label={
                <MKBox display="flex" alignItems="center" gap={1}>
                  <MKBox
                    component="img"
                    src={wechatPayLogo}
                    alt="WeChat Pay"
                    sx={{ height: 22, width: "auto" }}
                  />
                  <span>{t("payments.methods.wechat")}</span>
                </MKBox>
              }
            />
          ) : null}
          {availableMethods.includes("alipay") ? (
            <FormControlLabel
              value="alipay"
              control={<Radio />}
              label={
                <MKBox display="flex" alignItems="center" gap={1}>
                  <MKBox
                    component="img"
                    src={alipayLogo}
                    alt="Alipay"
                    sx={{ height: 22, width: "auto" }}
                  />
                  <span>{t("payments.methods.alipay")}</span>
                </MKBox>
              }
            />
          ) : null}
          {availableMethods.includes("card") ? (
            <FormControlLabel
              value="card"
              control={<Radio />}
              label={
                <MKBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <MKBox
                    component="img"
                    src={unionPayLogo}
                    alt="UnionPay"
                    sx={{ height: 22, width: "auto" }}
                  />
                  <MKBox
                    component="img"
                    src={masterCardLogo}
                    alt="MasterCard"
                    sx={{ height: 22, width: "auto" }}
                  />
                  <MKBox
                    component="img"
                    src={visaLogo}
                    alt="Visa"
                    sx={{ height: 22, width: "auto" }}
                  />
                  <MKBox
                    component="img"
                    src={amexLogo}
                    alt="American Express"
                    sx={{ height: 22, width: "auto" }}
                  />
                  <MKBox
                    component="img"
                    src={applepaylogo}
                    alt="Apple Pay"
                    sx={{ height: 22, width: "auto" }}
                  />
                  <span>{t("payments.methods.card")}</span>
                </MKBox>
              }
            />
          ) : null}
          {availableMethods.includes("alipayhk") ? (
            <FormControlLabel
              value="alipayhk"
              control={<Radio />}
              label={
                <MKBox display="flex" alignItems="center" gap={1}>
                  <MKBox
                    component="img"
                    src={alipayLogo}
                    alt="Alipay HK"
                    sx={{ height: 22, width: "auto" }}
                  />
                  <span>{t("payments.methods.alipayhk")}</span>
                </MKBox>
              }
            />
          ) : null}
        </RadioGroup>
        <MKBox mt={2}>
          <MKTypography variant="h6" fontWeight="bold">
            {t("payments.payOptions.providerTitle")}
          </MKTypography>

          {method === "card" ? (
            <RadioGroup
              value={provider || availableProviders[0] || ""}
              onChange={(e) => {
                if (onProviderChange) onProviderChange(e.target.value);
              }}
              sx={{ mt: 0.5 }}
            >
              {availableProviders.includes("stripe") ? (
                <FormControlLabel
                  value="stripe"
                  control={<Radio />}
                  label={t("payments.cardChannels.stripeIntent")}
                />
              ) : null}
              {availableProviders.includes("stripe_checkout") ? (
                <FormControlLabel
                  value="stripe_checkout"
                  control={<Radio />}
                  label={t("payments.cardChannels.stripeCheckout")}
                />
              ) : null}
              {availableProviders.includes("airwallex") ? (
                <FormControlLabel
                  value="airwallex"
                  control={<Radio />}
                  label={t("payments.providers.airwallex")}
                />
              ) : null}
            </RadioGroup>
          ) : (
            <RadioGroup
              value={provider || availableProviders[0] || ""}
              onChange={(e) => {
                if (onProviderChange) onProviderChange(e.target.value);
              }}
              sx={{ mt: 0.5 }}
            >
              {availableProviders.includes("stripe") ? (
                <FormControlLabel
                  value="stripe"
                  control={<Radio />}
                  label={t("payments.providers.stripeRecommended")}
                />
              ) : null}
              {method === "wechatpay" && provider === "stripe" ? (
                <MKBox
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mt: 1,
                    ml: 4,
                    p: 1.25,
                    bgcolor: "rgba(255, 243, 205, 0.7)",
                    borderRadius: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <MKTypography variant="body1" color="warning" fontWeight="bold">
                    ⚠️
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    color="warning"
                    fontWeight="bold"
                    sx={{ lineHeight: 1.45 }}
                  >
                    {t("payments.notes.wechatNoScreenshotForStripeAirwallex")}
                  </MKTypography>
                </MKBox>
              ) : null}
              {availableProviders.includes("airwallex") ? (
                <FormControlLabel
                  value="airwallex"
                  control={<Radio />}
                  label={
                    method === "alipayhk"
                      ? t("payments.methods.alipayhk")
                      : t("payments.providers.airwallex")
                  }
                />
              ) : null}
              {method === "wechatpay" && provider === "airwallex" ? (
                <MKBox
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mt: 1,
                    ml: 4,
                    p: 1.25,
                    bgcolor: "rgba(255, 243, 205, 0.7)",
                    borderRadius: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <MKTypography variant="body1" color="warning" fontWeight="bold">
                    ⚠️
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    color="warning"
                    fontWeight="bold"
                    sx={{ lineHeight: 1.45 }}
                  >
                    {t("payments.notes.wechatNoScreenshotForStripeAirwallex")}
                  </MKTypography>
                </MKBox>
              ) : null}
              {availableProviders.includes("epay") ? (
                <FormControlLabel
                  value="epay"
                  control={<Radio />}
                  label={t("payments.providers.epay")}
                />
              ) : null}
              {method === "wechatpay" && provider === "epay" ? (
                <MKBox
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mt: 1,
                    ml: 4,
                    p: 1.25,
                    bgcolor: "rgba(237, 247, 237, 0.7)",
                    borderRadius: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <MKTypography variant="body1" color="success" fontWeight="bold">
                    ✔️
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    color="success"
                    fontWeight="bold"
                    sx={{ lineHeight: 1.45 }}
                  >
                    {t("payments.notes.wechatEpayScreenshotAllowed")}
                  </MKTypography>
                </MKBox>
              ) : null}
            </RadioGroup>
          )}
        </MKBox>

        {error ? (
          <MKTypography variant="caption" color="error" display="block" mt={1}>
            {error}
          </MKTypography>
        ) : null}

        <MKBox mt={2} display="flex" gap={1} flexWrap="wrap">
          <MKButton
            variant="text"
            color="secondary"
            onClick={onCancel}
            disabled={!!disabled || !!loading}
          >
            {t("common.cancel")}
          </MKButton>

          <MKButton
            variant="contained"
            color="success"
            onClick={onContinue}
            disabled={!!disabled || !!loading || !!isPaymentPending}
          >
            {loading ? t("payments.payOptions.processing") : t("pricing.paymentModal.confirm")}
          </MKButton>

          {isPaymentPending ? (
            <MKButton
              variant="outlined"
              color="info"
              onClick={onRetry}
              disabled={!!retryDisabled || !!loading}
            >
              {t("payments.payOptions.retry")}
            </MKButton>
          ) : null}
        </MKBox>
      </MKBox>
    </>
  );
}

PayOptions.propTypes = {
  // payment selection
  provider: PropTypes.string,
  onProviderChange: PropTypes.func,
  method: PropTypes.string.isRequired,
  onMethodChange: PropTypes.func.isRequired,
  availableMethods: PropTypes.arrayOf(PropTypes.string),
  availableProviders: PropTypes.arrayOf(PropTypes.string),
  methodsLoading: PropTypes.bool,

  // actions
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  isPaymentPending: PropTypes.bool,
  retryDisabled: PropTypes.bool,

  // ui
  error: PropTypes.string,
};

PayOptions.defaultProps = {
  provider: "stripe",
  onProviderChange: null,
  onRetry: null,
  availableMethods: [],
  availableProviders: [],
  methodsLoading: false,
  loading: false,
  disabled: false,
  isPaymentPending: false,
  retryDisabled: false,

  error: "",
};

export default PayOptions;
